import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatEuro } from '@/lib/format';
import { updateLineRate } from '@/lib/estimating/service';

const money = (n) => Math.round((Number(n) || 0) * 100) / 100;

function RateCell({ line, onSaved }) {
  const [v, setV] = useState(line.selling_rate ?? '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (v === '' || v === null) return;
    const rate = Number(v);
    if (!Number.isFinite(rate) || rate < 0) { toast.error('Rate must be a number ≥ 0'); setV(line.selling_rate ?? ''); return; }
    if (rate === Number(line.selling_rate)) return;
    setBusy(true);
    try { onSaved(await updateLineRate(line, rate)); }
    catch (e) { toast.error(e.message); setV(line.selling_rate ?? ''); }
    finally { setBusy(false); }
  };
  return <input data-testid={`rate-input-${line.id}`} type="number" step="0.01" min="0" value={v} disabled={busy} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} className="h-8 w-28 rounded-md border border-border px-2 text-right text-sm tabular-nums focus:border-bronze focus:outline-none disabled:opacity-50" />;
}

// Editable selling-rate grid for a DRAFT priced BOQ: amounts = qty × rate, live trade subtotals and overall total.
export function RateGrid({ lines, onLineSaved }) {
  const groups = useMemo(() => {
    const m = new Map();
    (lines || []).forEach((l) => { if (!m.has(l.trade)) m.set(l.trade, []); m.get(l.trade).push(l); });
    return [...m.entries()].map(([trade, rows]) => ({ trade, rows, subtotal: money(rows.reduce((a, r) => a + (Number(r.selling_total) || 0), 0)) }));
  }, [lines]);
  const total = money(groups.reduce((a, g) => a + g.subtotal, 0));
  const unpriced = (lines || []).filter((l) => l.selling_rate == null && Number(l.quantity)).length;
  return (
    <div data-testid="boq-rate-grid" className="rounded-lg border border-border bg-white overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead className="w-12 text-xs">Row</TableHead><TableHead className="text-xs">Code</TableHead><TableHead className="text-xs">Ref</TableHead><TableHead className="text-xs">Description</TableHead>
          <TableHead className="text-xs">Unit</TableHead><TableHead className="text-xs text-right">Qty</TableHead><TableHead className="text-xs text-right">Rate (€)</TableHead><TableHead className="text-xs text-right">Amount</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {groups.map((g) => (
            <React.Fragment key={g.trade}>
              <TableRow className="bg-[#F7F5F1] hover:bg-[#F7F5F1]"><TableCell colSpan={8} className="text-xs font-semibold uppercase tracking-wider text-charcoal" data-testid={`trade-header-${g.trade}`}>{g.trade}</TableCell></TableRow>
              {g.rows.map((l) => (
                <TableRow key={l.id} data-testid={`boq-line-${l.id}`}>
                  <TableCell className="text-xs text-muted-foreground">{l.source_row}</TableCell><TableCell className="text-xs font-mono">{l.cost_code}</TableCell><TableCell className="text-xs">{l.item_ref || ''}</TableCell>
                  <TableCell className="text-sm">{l.description}</TableCell><TableCell className="text-sm">{l.unit || ''}</TableCell>
                  <TableCell className="text-sm text-right tabular-nums">{l.quantity != null ? Number(l.quantity) : ''}</TableCell>
                  <TableCell className="text-right"><RateCell line={l} onSaved={onLineSaved} /></TableCell>
                  <TableCell className="text-sm text-right tabular-nums" data-testid={`line-amount-${l.id}`}>{formatEuro(l.selling_total)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-white"><TableCell colSpan={7} className="text-xs text-right text-muted-foreground">Subtotal — {g.trade}</TableCell><TableCell className="text-sm text-right font-semibold tabular-nums" data-testid={`trade-subtotal-${g.trade}`}>{formatEuro(g.subtotal)}</TableCell></TableRow>
            </React.Fragment>
          ))}
          <TableRow className="bg-[#F7F5F1] hover:bg-[#F7F5F1]"><TableCell colSpan={7} className="text-sm text-right font-semibold">Tender total (ex VAT){unpriced ? <span className="ml-2 text-xs font-normal text-amber-700">{unpriced} line{unpriced > 1 ? 's' : ''} unpriced</span> : null}</TableCell><TableCell className="text-right font-display font-bold tabular-nums" data-testid="boq-tender-total">{formatEuro(total)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
